import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { getUser } from '../../App';

export default function UsersManager() {
  const currentUser = getUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { url, email }
  const [inviteError, setInviteError] = useState('');
  const [feedback, setFeedback] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/users')
      .then(({ data }) => setUsers(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteResult(null);
    setInviting(true);
    try {
      const { data } = await api.post('/api/auth/invite', {
        email: inviteEmail,
        name: inviteName,
      });
      setInviteResult({ url: data.inviteUrl, email: inviteEmail });
      setInviteEmail('');
      setInviteName('');
      load();
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Uitnodiging mislukt');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleToggle = async (user) => {
    const newRole = user.role === 'admin' ? 'student' : 'admin';
    try {
      await api.patch(`/api/users/${user.id}/role`, { role: newRole });
      setFeedback(`Rol van ${user.name || user.email} gewijzigd naar ${newRole}`);
      setTimeout(() => setFeedback(''), 3000);
      load();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Wijzigen mislukt');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const handleActiveToggle = async (user) => {
    try {
      await api.patch(`/api/users/${user.id}/active`, { is_active: !user.is_active });
      setFeedback(`${user.name || user.email} ${!user.is_active ? 'geactiveerd' : 'gedeactiveerd'}`);
      setTimeout(() => setFeedback(''), 3000);
      load();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Wijzigen mislukt');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div>
      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Gebruikers</h2>
          <p className="admin-section-sub">{users.length} gebruikers</p>
        </div>
      </div>

      {feedback && <div className="success-banner" style={{ marginBottom: 16 }}>{feedback}</div>}

      {/* Invite form */}
      <div className="invite-section">
        <h3 className="invite-title">Leerling uitnodigen</h3>
        <form onSubmit={handleInvite} className="invite-form-row">
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <input
              type="text"
              placeholder="Naam (optioneel)"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
            <input
              type="email"
              placeholder="E-mailadres leerling"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={inviting} style={{ whiteSpace: 'nowrap' }}>
            {inviting ? 'Versturen...' : 'Uitnodigen'}
          </button>
        </form>
        {inviteError && <div className="error-banner" style={{ marginTop: 8 }}>{inviteError}</div>}
        {inviteResult && (
          <div className="invite-result">
            <span>✓ Uitnodiging verzonden naar <strong>{inviteResult.email}</strong></span>
            <div style={{ marginTop: 6 }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Link (ook in logs):</span>
              <div className="invite-link-box">{inviteResult.url}</div>
            </div>
          </div>
        )}
      </div>

      {/* Users table */}
      {loading ? (
        <div className="admin-loading">Laden...</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Naam</th>
                <th>E-mailadres</th>
                <th>Rol</th>
                <th>Status</th>
                <th>Aangemaakt</th>
                <th style={{ width: 200 }}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px' }}>
                    Geen gebruikers gevonden
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td><strong>{u.name || '—'}</strong></td>
                    <td style={{ color: '#64748b' }}>{u.email}</td>
                    <td>
                      <span className={`badge badge-${u.role}`}>{u.role}</span>
                    </td>
                    <td>
                      {!u.is_active ? (
                        <span className="badge badge-inactive">Inactief</span>
                      ) : u.invite_pending && !u.has_password ? (
                        <span className="badge badge-pending">Uitnodiging verzonden</span>
                      ) : (
                        <span className="badge badge-active">Actief</span>
                      )}
                    </td>
                    <td style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{formatDate(u.created_at)}</td>
                    <td>
                      {u.id !== currentUser.id ? (
                        <div className="action-btns">
                          <button
                            className={`btn-sm ${u.role === 'admin' ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => handleRoleToggle(u)}
                            title={u.role === 'admin' ? 'Maak leerling' : 'Maak admin'}
                          >
                            {u.role === 'admin' ? '↓ Student' : '↑ Admin'}
                          </button>
                          <button
                            className={`btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => handleActiveToggle(u)}
                          >
                            {u.is_active ? 'Deactiveer' : 'Activeer'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Jij</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
